import type { TagStatus } from '@indeed/ifl-components'
import type {
  SchedulingPlan,
  SchedulingPlanLine,
} from 'syft-acp-core/store/agency-cascade-scheduling-plans/types'

export const authoredReasonLabels: Record<SchedulingPlan['authored_reason'], string> = {
  accepted: 'Agency Accepted',
  shift_added: 'Shift added',
  shift_rescheduled: 'Shift rescheduled',
  reallocated: 'Reallocated',
}

export const lineStateLabels: Record<SchedulingPlanLine['state'], string> = {
  scheduled: 'Scheduled',
  not_enforceable: 'Not enforceable',
  met: 'Met',
  missed: 'Missed',
  cascaded: 'Cascaded',
  removed_by_admin: 'Removed by admin',
  released_by_agency: 'Released by agency',
  removed_by_client: 'Removed by client',
  shift_cancelled: 'Shift cancelled',
  revoked: 'Revoked',
}

/** Every removal cause shares one colour: the allocation is gone, whoever took it. */
export const lineStateTagStatuses: Record<SchedulingPlanLine['state'], TagStatus> = {
  scheduled: 'info',
  not_enforceable: 'neutral',
  met: 'success',
  missed: 'critical',
  cascaded: 'warning',
  removed_by_admin: 'warning',
  released_by_agency: 'warning',
  removed_by_client: 'warning',
  shift_cancelled: 'warning',
  revoked: 'warning',
}

/**
 * Readable form of a wire value that has no label yet, e.g. `skipped_past` → "Skipped past". flex-core
 * can add values ahead of ACP, so lookups fall back to this instead of rendering nothing.
 */
export const humanizeWireValue = (value: string) => {
  const words = value.replace(/_/g, ' ').trim()

  return words.charAt(0).toUpperCase() + words.slice(1)
}

const labelOrHumanized = (labels: Record<string, string>, value: string) =>
  labels[value] ?? humanizeWireValue(value)

export const getAuthoredReasonLabel = (reason: string) => labelOrHumanized(authoredReasonLabels, reason)

export const getLineStateLabel = (state: string) => labelOrHumanized(lineStateLabels, state)

export const getLineStateTagStatus = (state: string): TagStatus =>
  (lineStateTagStatuses as Record<string, TagStatus>)[state] ?? 'neutral'

export const MET_LATER_UNFILLED_LABEL = 'Met - later unfilled'
export const MET_LATER_UNFILLED_TOOLTIP =
  'The booking deadline was met, but the shift has since lost workers.'

/**
 * A met deadline stays met in flex-core, but `workers_provided` is the live count, so the shift can
 * have dropped below its allocation since. The cascade does not revisit met lines, so ACP flags it.
 */
export const isMetLaterUnfilled = (
  line: Pick<SchedulingPlanLine, 'state' | 'workers_provided' | 'allocation_without_buffer'>,
) => line.state === 'met' && line.workers_provided < line.allocation_without_buffer
